/**
 * Report Data Transformer
 * 
 * Transforms raw report data from the backend into the standardized format
 * expected by the BaseReportTemplate component.
 */

export interface StandardizedReportData {
  summary: {
    dateRange: {
      start: string;
      end: string;
    };
    metrics: Record<string, number | string>;
    overview?: string;
  };
  charts?: Array<{
    type: 'line' | 'area' | 'bar' | 'pie' | 'funnel';
    title: string;
    data: any[];
  }>;
  tables?: Array<{
    title: string;
    headers: string[];
    rows: any[];
  }>;
  insights?: {
    summary?: string;
    keyFindings?: string[];
    recommendations?: string[];
    trends?: string[];
  };
  trends?: Record<string, string>; // Legacy support
  customData?: Record<string, any>; // For custom reports
}

/**
 * Safely parse JSON data
 */
function safeJsonParse(data: any): any {
  if (!data) return null;
  
  try {
    // If data is already an object, return it
    if (typeof data === 'object' && data !== null) {
      return data;
    }
    
    // If data is a string, try to parse it as JSON
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    
    return data;
  } catch (error) {
    console.error('Error parsing JSON data:', error);
    return null;
  }
}

/**
 * Transform legacy or malformed data into standardized format
 */
function transformLegacyData(data: any): StandardizedReportData | null {
  if (!data) return null;

  console.log('Attempting legacy transformation for:', Object.keys(data));

  // Handle case where data might be a flat object with mixed properties
  const transformed: StandardizedReportData = {
    summary: {
      dateRange: {
        start: data.startDate || data.dateRange?.start || data.summary?.dateRange?.start || '2024-01-01',
        end: data.endDate || data.dateRange?.end || data.summary?.dateRange?.end || '2024-01-31'
      },
      metrics: {}
    }
  };

  // Extract metrics from various possible locations
  if (data.summary?.metrics) {
    transformed.summary.metrics = { ...data.summary.metrics };
  } else if (data.metrics) {
    transformed.summary.metrics = { ...data.metrics };
  } else {
    // Try to extract metrics from top-level properties
    const metricKeys = ['sessions', 'users', 'pageviews', 'bounceRate', 'conversionRate', 'revenue'];
    metricKeys.forEach(key => {
      if (data[key] !== undefined) {
        transformed.summary.metrics[key] = data[key];
      }
    });
  }

  // Handle charts
  if (data.charts && Array.isArray(data.charts)) {
    transformed.charts = data.charts;
  } else if (data.visualizations && Array.isArray(data.visualizations)) {
    transformed.charts = data.visualizations;
  }

  // Handle tables
  if (data.tables && Array.isArray(data.tables)) {
    transformed.tables = data.tables;
  } else if (data.reports && Array.isArray(data.reports)) {
    transformed.tables = data.reports;
  }

  // Handle insights
  if (data.insights) {
    transformed.insights = data.insights;
  }

  // Handle trends
  if (data.trends) {
    transformed.trends = data.trends;
  }

  // Handle custom data
  if (data.customData) {
    transformed.customData = data.customData;
  }

  // Add overview if available
  if (data.overview || data.summary?.overview) {
    transformed.summary.overview = data.overview || data.summary.overview;
  }

  console.log('Legacy transformation result:', {
    hasMetrics: Object.keys(transformed.summary.metrics).length > 0,
    hasCharts: !!transformed.charts,
    hasTables: !!transformed.tables,
    hasInsights: !!transformed.insights,
    hasTrends: !!transformed.trends,
    hasCustomData: !!transformed.customData
  });

  return transformed;
}

/**
 * Transform mock/example data that might be in JSON format
 */
function transformMockData(data: any): StandardizedReportData | null {
  // Handle case where data contains mock table structure like:
  // { "rows": [...], "headers": [...] }
  if (data.rows && data.headers && Array.isArray(data.rows) && Array.isArray(data.headers)) {
    return {
      summary: {
        dateRange: {
          start: '2024-01-01',
          end: '2024-01-31'
        },
        metrics: {
          totalRecords: data.rows.length
        }
      },
      tables: [{
        title: 'Data Table',
        headers: data.headers,
        rows: data.rows
      }]
    };
  }

  // Handle case where data is just an array of objects
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0];
    if (typeof firstItem === 'object') {
      const headers = Object.keys(firstItem);
      const rows = data.map(item => headers.map(header => item[header]));
      
      return {
        summary: {
          dateRange: {
            start: '2024-01-01',
            end: '2024-01-31'
          },
          metrics: {
            totalRecords: data.length
          }
        },
        tables: [{
          title: 'Data Table',
          headers,
          rows
        }]
      };
    }
  }

  return null;
}

/**
 * Generate fallback data when no valid data is available
 */
function generateFallbackData(reportType: string): StandardizedReportData {
  const baseData: StandardizedReportData = {
    summary: {
      dateRange: {
        start: '2024-01-01',
        end: '2024-01-31'
      },
      metrics: {}
    }
  };

  switch (reportType) {
    case 'analytics':
      baseData.summary.metrics = {
        sessions: 0,
        users: 0,
        pageviews: 0,
        bounceRate: 0,
        conversionRate: 0
      };
      baseData.charts = [{
        type: 'line',
        title: 'Sessions Over Time',
        data: []
      }];
      break;

    case 'conversion':
      baseData.summary.metrics = {
        totalConversions: 0,
        conversionRate: 0,
        conversionValue: 0
      };
      baseData.charts = [{
        type: 'funnel',
        title: 'Conversion Funnel',
        data: []
      }];
      break;

    case 'performance':
      baseData.summary.metrics = {
        avgLoadTime: 0,
        mobileScore: 0,
        desktopScore: 0
      };
      break;

    case 'audience':
      baseData.summary.metrics = {
        totalUsers: 0,
        newUsers: 0,
        returningUsers: 0
      };
      break;

    case 'seo':
      baseData.summary.metrics = {
        organicSessions: 0,
        avgPosition: 0,
        totalImpressions: 0
      };
      break;

    case 'social':
      baseData.summary.metrics = {
        socialSessions: 0,
        engagementRate: 0,
        reachGrowth: 0
      };
      break;

    default:
      baseData.summary.metrics = {
        totalMetrics: 0
      };
  }

  return baseData;
}

/**
 * Main transformer function
 * 
 * Takes raw report data and transforms it into the standardized format
 * expected by BaseReportTemplate
 */
export function transformReportData(
  rawData: any, 
  reportType: string = 'custom'
): StandardizedReportData {
  console.log('Transforming report data:', { rawData, reportType });

  // Step 1: Parse JSON if needed
  const parsedData = safeJsonParse(rawData);
  if (!parsedData) {
    console.warn('No valid data found, generating fallback');
    return generateFallbackData(reportType);
  }

  console.log('Parsed data keys:', Object.keys(parsedData));

  // Step 2: Check if data is already in correct format
  if (parsedData.summary && parsedData.summary.metrics && Object.keys(parsedData.summary.metrics).length > 0) {
    console.log('Data already in correct format');
    return parsedData as StandardizedReportData;
  }

  // Step 3: Handle proper analytics/performance/conversion reports with charts and tables
  if (parsedData.summary || parsedData.charts || parsedData.tables) {
    console.log('Detected structured report format, processing...');
    console.log('Available sections:', {
      hasSummary: !!parsedData.summary,
      hasCharts: !!parsedData.charts,
      hasTables: !!parsedData.tables,
      hasInsights: !!parsedData.insights,
      hasCustomData: !!parsedData.customData
    });
    
    // Build the proper structure
    const structuredData: StandardizedReportData = {
      summary: {
        dateRange: parsedData.summary?.dateRange || {
          start: '2024-01-01',
          end: '2024-01-31'
        },
        metrics: parsedData.summary?.metrics || {}
      }
    };

    console.log('Created base summary with metrics:', Object.keys(structuredData.summary.metrics));

    // Copy over all the sections that exist
    if (parsedData.charts && Array.isArray(parsedData.charts)) {
      structuredData.charts = parsedData.charts;
      console.log('Added charts:', parsedData.charts.length);
    }
    
    if (parsedData.tables && Array.isArray(parsedData.tables)) {
      structuredData.tables = parsedData.tables;
      console.log('Added tables:', parsedData.tables.length);
    }
    
    if (parsedData.insights) {
      structuredData.insights = parsedData.insights;
      console.log('Added insights');
    }
    
    if (parsedData.trends) {
      structuredData.trends = parsedData.trends;
      console.log('Added trends:', Object.keys(parsedData.trends).length);
    }
    
    if (parsedData.customData) {
      structuredData.customData = parsedData.customData;
      console.log('Added customData:', Object.keys(parsedData.customData).length);
    }
    
    if (parsedData.summary?.overview) {
      structuredData.summary.overview = parsedData.summary.overview;
      console.log('Added overview');
    }

    console.log('Structured format transformation result:', {
      summaryMetricsCount: Object.keys(structuredData.summary.metrics).length,
      hasDateRange: !!structuredData.summary.dateRange,
      hasCharts: !!structuredData.charts,
      hasTables: !!structuredData.tables,
      dateRangeStart: structuredData.summary.dateRange.start,
      dateRangeEnd: structuredData.summary.dateRange.end
    });
    
    return structuredData;
  }

  // Step 4: Handle custom reports with only customData (widgets)
  if (parsedData.customData && Object.keys(parsedData.customData).length > 0) {
    console.log('Detected custom report format, extracting from customData...');
    
    let metrics: Record<string, any> = {};
    
    // Extract metrics from customData for custom reports only
    Object.entries(parsedData.customData).forEach(([key, value]: [string, any]) => {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Look for numeric values that could be metrics
        Object.entries(value).forEach(([metricKey, metricValue]: [string, any]) => {
          if (typeof metricValue === 'number') {
            const cleanKey = `${key}_${metricKey}`.replace(/-/g, '_');
            metrics[cleanKey] = metricValue;
          }
        });
      } else if (typeof value === 'number') {
        // Direct numeric value
        const cleanKey = key.replace(/-/g, '_');
        metrics[cleanKey] = value;
      } else if (Array.isArray(value) && value.length > 0) {
        // Array data - use length as a metric
        const cleanKey = `${key}_count`.replace(/-/g, '_');
        metrics[cleanKey] = value.length;
      }
    });
    
    console.log('Extracted metrics from customData:', metrics);
    
    const customTransformed: StandardizedReportData = {
      summary: {
        dateRange: parsedData.summary?.dateRange || {
          start: '2024-01-01',
          end: '2024-01-31'
        },
        metrics: metrics
      }
    };

    // Copy over other sections
    if (parsedData.insights) {
      customTransformed.insights = parsedData.insights;
    }
    if (parsedData.trends) {
      customTransformed.trends = parsedData.trends;
    }
    if (parsedData.customData) {
      customTransformed.customData = parsedData.customData;
    }

    console.log('Custom format transformation result:', {
      summaryMetricsCount: Object.keys(customTransformed.summary.metrics).length,
      hasDateRange: !!customTransformed.summary.dateRange
    });
    
    return customTransformed;
  }

  // Step 5: Try to transform legacy data
  const legacyTransformed = transformLegacyData(parsedData);
  if (legacyTransformed && Object.keys(legacyTransformed.summary.metrics).length > 0) {
    console.log('Successfully transformed legacy data');
    return legacyTransformed;
  }

  // Step 6: Try to transform mock/example data
  const mockTransformed = transformMockData(parsedData);
  if (mockTransformed) {
    console.log('Successfully transformed mock data');
    return mockTransformed;
  }

  // Step 7: Generate fallback data
  console.warn('Could not transform data, using fallback');
  return generateFallbackData(reportType);
}

/**
 * Validate that report data has the required structure
 */
export function validateReportData(data: any): boolean {
  if (!data || typeof data !== 'object') {
    console.log('Validation failed: no data or not object');
    return false;
  }
  
  // Must have summary
  if (!data.summary) {
    console.log('Validation failed: no summary');
    return false;
  }
  
  // Must have metrics (but can be empty object)
  if (!data.summary.metrics || typeof data.summary.metrics !== 'object') {
    console.log('Validation failed: no metrics or metrics not object');
    return false;
  }
  
  // Must have dateRange with start and end
  if (!data.summary.dateRange) {
    console.log('Validation failed: no dateRange');
    return false;
  }
  
  if (!data.summary.dateRange.start || !data.summary.dateRange.end) {
    console.log('Validation failed: missing start or end date', {
      start: data.summary.dateRange.start,
      end: data.summary.dateRange.end
    });
    return false;
  }
  
  console.log('Validation passed');
  return true;
}

/**
 * Get sample data for testing
 */
export function getSampleReportData(reportType: string): StandardizedReportData {
  const sampleData = {
    analytics: {
      summary: {
        dateRange: { start: '2024-01-01', end: '2024-01-31' },
        metrics: {
          sessions: 15420,
          users: 8932,
          pageviews: 45230,
          bounceRate: 0.42,
          conversionRate: 0.032
        }
      },
      charts: [
        {
          type: 'line' as const,
          title: 'Sessions Over Time',
          data: [
            { date: '2024-01-01', value: 450 },
            { date: '2024-01-02', value: 523 },
            { date: '2024-01-03', value: 478 }
          ]
        }
      ],
      tables: [
        {
          title: 'Top Pages',
          headers: ['Page', 'Views', 'Bounce Rate'],
          rows: [
            ['/home', '8,500', '35%'],
            ['/products', '6,200', '28%'],
            ['/about', '4,800', '45%']
          ]
        }
      ]
    }
  };

  return sampleData[reportType as keyof typeof sampleData] || generateFallbackData(reportType);
} 